import React from 'react';
import { Table, Card, Avatar, Space, Typography, Button, Row, Col, Grid } from 'antd';
import { UserOutlined, EditOutlined, EyeOutlined, PhoneOutlined, BankOutlined } from '@ant-design/icons';
import type { User } from '../../../types/users';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface UsersTableProps {
  users: User[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number) => void;
  };
  onEdit: (user: User) => void;
  onView: (user: User) => void;
  onTableChange: (page: number) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading,
  pagination,
  onEdit,
  onView,
  onTableChange,
}) => {
  const { t } = useTranslation(['users', 'common']);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const columns = [
    {
      title: t('users:name'),
      dataIndex: 'displayName',
      key: 'displayName',
      render: (text: string, record: User) => (
        <Space>
          <Avatar 
            src={record.profilePicture?.url} 
            icon={<UserOutlined />}
          />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: t('users:email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('users:phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (text: string) => text ? (
        <Space>
          <PhoneOutlined />
          <Text>{text}</Text>
        </Space>
      ) : '-',
    },
    {
      title: t('users:workspace'),
      dataIndex: ['workspace', 'name'],
      key: 'workspace',
      render: (text: string) => (
        <Space>
          <BankOutlined />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: t('users:role'),
      dataIndex: 'roles',
      key: 'roles',
      render: (roles: string[]) => roles.join(', '),
    },
    {
      title: t('users:status'),
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: t('common:actions'),
      key: 'actions',
      render: (_: any, record: User) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          />
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => onView(record)}
          />
        </Space>
      ),
    },
  ];

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {users.map((user) => (
          <Card key={user.id} hoverable>
            <Row gutter={[16, 16]} align="middle">
              <Col>
                <Avatar 
                  size={48}
                  src={user.profilePicture?.url} 
                  icon={<UserOutlined />}
                />
              </Col>
              <Col flex="auto">
                <Space direction="vertical" size={0}>
                  <Text strong>{user.displayName}</Text>
                  <Text type="secondary">{user.email}</Text>
                  {user.phoneNumber && (
                    <Space>
                      <PhoneOutlined />
                      <Text type="secondary">{user.phoneNumber}</Text>
                    </Space>
                  )}
                  <Space>
                    <BankOutlined />
                    <Text type="secondary">{user.workspace?.name}</Text>
                  </Space>
                  <Text type="secondary">{user.roles.join(', ')}</Text>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => onEdit(user)}
                  />
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => onView(user)}
                  />
                </Space>
              </Col>
            </Row>
          </Card>
        ))}
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Button
            type="link"
            onClick={() => onTableChange(pagination.current + 1)}
            disabled={!pagination.total || pagination.current * pagination.pageSize >= pagination.total}
          >
            {t('common:loadMore')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={users}
      loading={loading}
      pagination={pagination}
      onChange={(pagination) => onTableChange(pagination.current || 1)}
      rowKey="id"
    />
  );
};

export default UsersTable; 